import type { BusinessHero, BusinessIntro, BusinessSection } from './types'

/**
 * BusinessPageV2 — content map (zh / en)
 * Visual reference: https://baristamap.coffee/pages/business
 *
 * Three business pillars:
 *  1. 門店  → /{lang}/store/xinyi
 *  2. 網購  → /{lang}/shop  (specifically coffee beans)
 *  3. 批發  → /{lang}/wholesale
 */

export const businessHero: BusinessHero = {
  image: 'https://file.lumi.new/p373839333138763776/SPF2016978765358108672.webp',
  imageAlt: {
    zh: 'ONGU Studio 自家烘焙咖啡',
    en: 'ONGU Studio coffee roasting',
  },
  title: {
    zh: 'Our Business',
    en: 'Our Business',
  },
}

export const businessIntro: BusinessIntro = {
  eyebrow: {
    zh: '從信義區的小焙煎所，向世界延伸',
    en: 'From a small Xinyi roastery to the world',
  },
  headline: {
    zh: 'Brewing Connections',
    en: 'Brewing Connections',
  },
  body: {
    zh: 'ONGU Studio 從一間自家烘焙咖啡廳出發。我們相信「至福的咖啡體驗，源自人與人的連結」——精選世界各地的單一產區生豆、自家烘焙、搭配職人手沖與義式沖煮，並延伸出咖啡豆網購、餐飲與企業批發、品牌顧問等多元事業。我們不只賣豆子，更與每一位夥伴一起，把每一杯咖啡的故事好好說完。',
    en: 'ONGU Studio began as a single in-house roastery café. We believe great coffee comes from genuine connection — between farmer, roaster, barista and guest. From single-origin sourcing and small-batch roasting, to retail bean shop, B2B wholesale and brand consultancy, we craft every cup so that the story behind it can be fully told.',
  },
  image: 'https://file.lumi.new/p373839333138763776/SPF2016978764204675072.webp',
  imageAlt: {
    zh: 'ONGU Studio 信義店外觀',
    en: 'ONGU Studio Xinyi storefront',
  },
}

export const businessSections: BusinessSection[] = [
  {
    key: 'store',
    eyebrow: 'ONGU Studio Xinyi',
    title: {
      zh: '信義旗艦門店',
      en: 'Xinyi Flagship Store',
    },
    subtitle: {
      zh: '自家烘焙・職人手沖',
      en: 'In-house Roasting・Hand Brew',
    },
    description: {
      zh: '位於台北信義區的旗艦門店，結合烘豆所、咖啡吧台與品牌展示。每日為客人現點現沖單一產區手沖咖啡、義式飲品與限定豆款品飲。空間以鋁銀 × 黑極簡工業風打造，是體驗 ONGU Studio 風味宇宙的第一站。',
      en: 'Our flagship store in Xinyi, Taipei, brings together the roastery, the bar and the brand showroom. Guests can enjoy single-origin pour-overs, espresso drinks and limited bean tastings every day, all set in a minimalist aluminium-and-black industrial space — the first chapter of the ONGU flavour universe.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2016978764204675072.webp',
    imageAlt: {
      zh: 'ONGU Studio 信義店內景',
      en: 'ONGU Studio Xinyi interior',
    },
    cta: { zh: 'Read more', en: 'Read more' },
    hrefPath: '/store/xinyi/v2',
    mediaPosition: 'right',
  },
  {
    key: 'shop',
    eyebrow: 'Online Bean Shop',
    title: {
      zh: '精品咖啡豆網購',
      en: 'Specialty Coffee Beans',
    },
    subtitle: {
      zh: '單一產區・濾掛・禮盒',
      en: 'Single Origin・Drip Bag・Gift Box',
    },
    description: {
      zh: '想把信義店的味道帶回家？我們的線上咖啡豆網購提供當週新鮮烘焙的單一產區咖啡豆、濾掛包與禮盒組，從衣索比亞、瓜地馬拉到肯亞、巴拿馬，依照不同烘焙度與風味描述細分，並附上完整豆單規格（產區/海拔/處理法/風味），讓你在家也能找到屬於自己的一杯。',
      en: 'Want to bring the Xinyi cup home? Our online bean shop ships freshly roasted single-origin coffees, drip-bag sets and curated gift boxes — from Ethiopia and Guatemala to Kenya and Panama. Each bag comes with a full spec card (origin, altitude, process, tasting notes) so you can find the cup that belongs to you.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2016947172312682496.png',
    imageAlt: {
      zh: 'ONGU Studio 精品咖啡豆',
      en: 'ONGU Studio specialty coffee beans',
    },
    cta: { zh: 'Read more', en: 'Read more' },
    hrefPath: '/shop',
    mediaPosition: 'left',
  },
  {
    key: 'wholesale',
    eyebrow: 'B2B Wholesale',
    title: {
      zh: '餐飲批發合作',
      en: 'Wholesale Partnership',
    },
    subtitle: {
      zh: '咖啡廳・餐廳・企業',
      en: 'Café・Restaurant・Enterprise',
    },
    description: {
      zh: '為咖啡廳、餐廳、旅宿與企業客戶提供穩定且具個性的咖啡解決方案：客製專屬配方豆、濾掛 OEM、義式機具導入、菜單顧問與吧台教育訓練。我們也提供階梯價（5kg/30kg）、月結帳期與專屬報價單，讓你的店家能用一杯有故事的咖啡，留住每一位客人。',
      en: 'Specialty coffee solutions for cafés, restaurants, hotels and enterprise clients: custom blends, drip-bag OEM, espresso equipment integration, menu consulting and barista training. With tiered pricing (5kg/30kg), monthly billing terms and dedicated quotation, we help your venue serve a cup of coffee that keeps guests coming back.',
    },
    image: 'https://file.lumi.new/p373839333138763776/SPF2016978766800949248.webp',
    imageAlt: {
      zh: 'ONGU Studio B2B 批發合作',
      en: 'ONGU Studio B2B wholesale partnership',
    },
    cta: { zh: 'Read more', en: 'Read more' },
    hrefPath: '/wholesale',
    mediaPosition: 'right',
  },
]
